// src/pages/admin/CompanyManagement.js
import React, { useState, useEffect } from 'react';
import { companyService } from '../../services/apiServices';
import { subscriptionService } from '../../services/subscriptionService';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';
import PasswordRequirements from '../../components/common/PasswordRequirements';
import { validatePassword } from '../../utils/passwordValidator';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/admin/StatusBadge';
import SearchFilterBar from '../../components/admin/SearchFilterBar';
import DataTable from '../../components/admin/DataTable';
import ConfirmationDialog from '../../components/admin/ConfirmationDialog';
import Pagination from '../../components/admin/Pagination';

// Enhanced styles with animations and transitions
const formStyles = `
  /* Form Container Animations */
  .enhanced-company-form {
    animation: slideInFromTop 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform-origin: top;
  }
  
  @keyframes slideInFromTop {
    0% {
      opacity: 0;
      transform: translateY(-20px) scale(0.98);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  /* Section Headers with Animated Icons */
  .enhanced-company-form .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid transparent;
    background: linear-gradient(90deg, #e5e7eb, transparent) bottom;
    background-size: 100% 2px;
    background-repeat: no-repeat;
    transition: all 0.3s ease;
    position: relative;
  }
  
  .enhanced-company-form .section-header::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #3b82f6, #1d4ed8);
    transition: width 0.5s ease;
  }
  
  .enhanced-company-form .section-header:hover::after {
    width: 100%;
  }
  
  .enhanced-company-form .section-icon {
    margin-right: 0.75rem;
    font-size: 1.2rem;
    transition: all 0.3s ease;
    animation: pulse 2s infinite;
  }
  
  .enhanced-company-form .section-header:hover .section-icon {
    transform: scale(1.1) rotate(5deg);
    animation: none;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  
  /* Form Sections with Hover Effects */
  .enhanced-company-form .form-section {
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    border: 1px solid transparent;
    position: relative;
    overflow: hidden;
  }
  
  .enhanced-company-form .form-section::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.05), transparent);
    transition: left 0.6s ease;
  }
  
  .enhanced-company-form .form-section:hover::before {
    left: 100%;
  }
  
  .enhanced-company-form .form-section:hover {
    border-color: #3b82f6;
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
    transform: translateY(-2px);
  }
  
  /* Input Field Animations */
  .enhanced-input {
    transition: all 0.3s ease;
    position: relative;
  }
  
  .enhanced-input:focus {
    transform: scale(1.02);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  /* Button Animations */
  .animated-button {
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    overflow: hidden;
  }
  
  .animated-button::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: width 0.6s ease, height 0.6s ease;
  }
  
  .animated-button:hover::before {
    width: 300px;
    height: 300px;
  }
  
  .animated-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  
  .animated-button:active {
    transform: translateY(0);
  }
  
  /* User Table Animations */
  .user-table {
    max-height: 350px;
    overflow-y: auto;
  }
  
  .user-row {
    animation: slideInFromLeft 0.3s ease;
    transition: all 0.3s ease;
  }
  
  .user-row:hover {
    background: linear-gradient(90deg, rgba(59, 130, 246, 0.05), transparent);
    transform: translateX(5px);
  }
  
  @keyframes slideInFromLeft {
    0% {
      opacity: 0;
      transform: translateX(-20px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  /* Subscription Summary with Gradient Animation */
  .subscription-summary {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #7c3aed 100%);
    background-size: 200% 200%;
    animation: gradientShift 4s ease infinite;
    color: white;
    padding: 1.5rem;
    border-radius: 0.75rem;
    margin-top: 1rem;
    position: relative;
    overflow: hidden;
  }
  
  .subscription-summary::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    transform: translateX(-100%);
    animation: shimmer 3s infinite;
  }
  
  @keyframes gradientShift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  
  /* Metrics Cards Animation */
  .metric-card {
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    cursor: pointer;
  }
  
  .metric-card:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
  
  .metric-number {
    animation: countUp 1s ease-out;
  }
  
  @keyframes countUp {
    0% { opacity: 0; transform: translateY(20px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  /* Form Toggle Animation */
  .form-toggle-enter {
    animation: expandIn 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  
  .form-toggle-exit {
    animation: collapseOut 0.3s ease-in;
  }
  
  @keyframes expandIn {
    0% {
      opacity: 0;
      max-height: 0;
      transform: scaleY(0);
    }
    100% {
      opacity: 1;
      max-height: 1000px;
      transform: scaleY(1);
    }
  }
  
  @keyframes collapseOut {
    0% {
      opacity: 1;
      max-height: 1000px;
      transform: scaleY(1);
    }
    100% {
      opacity: 0;
      max-height: 0;
      transform: scaleY(0);
    }
  }
  
  /* Loading Spinner Enhancement */
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    backdrop-filter: blur(2px);
  }
  
  /* Success/Error Message Animations */
  .message-slide-in {
    animation: slideInFromRight 0.4s ease;
  }
  
  @keyframes slideInFromRight {
    0% {
      opacity: 0;
      transform: translateX(100px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  /* Stagger Animation for Form Fields */
  .form-field {
    animation: staggerIn 0.4s ease;
  }
  
  .form-field:nth-child(1) { animation-delay: 0.1s; }
  .form-field:nth-child(2) { animation-delay: 0.2s; }
  .form-field:nth-child(3) { animation-delay: 0.3s; }
  .form-field:nth-child(4) { animation-delay: 0.4s; }
  
  @keyframes staggerIn {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Responsive Animations */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

const CompanyManagement = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCompanyId, setCurrentCompanyId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    status: 'active',
    // Subscription data
    subscription: {
      status: 'active',
      startDate: new Date().toISOString().split('T')[0], // Today's date
      endDate: '',
      paymentStatus: 'pending',
      aiAccess: 'both',
      aiEnabled: true,
      notes: ''
    },
    // Users to be created with the company
    users: []
  });

  // User form for adding users to the company
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'simpleuser'
  });

  const [showUserForm, setShowUserForm] = useState(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);

  // Load data on mount and when filters/page change
  useEffect(() => {
    loadData();
  }, [pagination.page, pagination.limit, searchTerm, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Prepare filters for API - only include defined values
      const filters = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (searchTerm) filters.search = searchTerm;
      if (statusFilter !== 'all') filters.status = statusFilter;

      // Load companies and their subscription data
      const [companiesResponse, subscriptionsResponse] = await Promise.allSettled([
        companyService.getCompanies(filters),
        subscriptionService.getSubscriptions()
      ]);

      let companiesData = [];
      let subscriptionsData = [];

      // Handle companies data
      if (companiesResponse.status === 'fulfilled' && companiesResponse.value.success) {
        companiesData = companiesResponse.value.data || [];
        // Update pagination from response if available
        if (companiesResponse.value.pagination) {
          setPagination(prev => ({
            ...prev,
            ...companiesResponse.value.pagination
          }));
        } else {
          // Fallback if API doesn't return pagination yet (though it should)
          setPagination(prev => ({
            ...prev,
            total: companiesData.length,
            pages: Math.ceil(companiesData.length / prev.limit)
          }));
        }
      } else {
        throw new Error('Impossible de charger les données des entreprises');
      }

      // Handle subscriptions data (optional)
      if (subscriptionsResponse.status === 'fulfilled' && subscriptionsResponse.value.success) {
        subscriptionsData = subscriptionsResponse.value.data || [];
      }

      // Normalize and merge data
      const normalizedCompanies = companiesData.map(company => {
        const companyId = company.id || company._id;
        const subscription = subscriptionsData.find(sub =>
          sub.companyId === companyId || sub.companyId === company._id
        );

        return {
          ...company,
          id: companyId,
          subscription: subscription || {
            status: 'inactive',
            startDate: null,
            endDate: null,
            daysRemaining: 0
          }
        };
      });

      setCompanies(normalizedCompanies);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    if (field.startsWith('subscription.')) {
      const subscriptionField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        subscription: {
          ...prev.subscription,
          [subscriptionField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleUserFormChange = (field, value) => {
    setUserForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addUserToCompany = () => {
    if (!userForm.name.trim() || !userForm.email.trim() || !userForm.password.trim()) {
      showErrorToast('Tous les champs utilisateur sont requis');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userForm.email)) {
      showErrorToast('Format d\'email invalide');
      return;
    }

    // Check if email already exists in the users list
    if (formData.users.some(user => user.email === userForm.email)) {
      showErrorToast('Un utilisateur avec cet email existe déjà dans la liste');
      return;
    }

    // Validate password complexity
    if (!validatePassword(userForm.password).isValid) {
      showErrorToast('Le mot de passe doit comporter au moins 8 caractères, une majuscule, une minuscule, un chiffre et un symbole');
      return;
    }

    const newUser = {
      id: Date.now(), // Temporary ID for the list
      ...userForm
    };

    setFormData(prev => ({
      ...prev,
      users: [...prev.users, newUser]
    }));

    // Reset user form
    setUserForm({
      name: '',
      email: '',
      password: '',
      role: 'simpleuser'
    });
    setShowUserForm(false);
    showSuccessToast('Utilisateur ajouté à la liste');
  };

  const removeUserFromCompany = (userId) => {
    setFormData(prev => ({
      ...prev,
      users: prev.users.filter(user => user.id !== userId)
    }));
    showSuccessToast('Utilisateur retiré de la liste');
  };

  const calculateSubscriptionDays = () => {
    if (!formData.subscription.startDate || !formData.subscription.endDate) return 0;
    const start = new Date(formData.subscription.startDate);
    const end = new Date(formData.subscription.endDate);
    const diffTime = end - start;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      let response;
      if (isEditing) {
        response = await companyService.updateCompany(currentCompanyId, formData);
      } else {
        response = await companyService.createCompany(formData);
      }

      if (response.success) {
        const successMessage = isEditing
          ? 'Entreprise mise à jour avec succès!'
          : (response.data.message || 'Entreprise créée avec succès!');

        showSuccessToast(successMessage);

        // Show additional info for created users
        if (!isEditing && response.data.usersCreated && response.data.usersCreated.length > 0) {
          setTimeout(() => {
            showSuccessToast(
              `${response.data.usersCreated.length} utilisateur(s) créé(s) avec l'entreprise`,
              { duration: 4000 }
            );
          }, 1000);
        }

        await loadData();
        resetForm();
      } else {
        throw new Error(response.message || 'Échec de l\'opération');
      }
    } catch (err) {
      const errorMessage = `Erreur lors de ${isEditing ? 'la mise à jour' : 'la création'}: ${err.message}`;
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (company) => {
    setFormData({
      name: company.name,
      domain: company.domain || '',
      status: company.status,
      subscription: {
        status: company.subscription?.status || 'inactive',
        startDate: company.subscription?.startDate ? new Date(company.subscription.startDate).toISOString().split('T')[0] : '',
        endDate: company.subscription?.endDate ? new Date(company.subscription.endDate).toISOString().split('T')[0] : '',
        paymentStatus: company.subscription?.paymentStatus || 'pending',
        aiAccess: company.subscription?.aiAccess || 'both',
        aiEnabled: company.subscription?.aiEnabled ?? true,
        notes: company.subscription?.notes || ''
      },
      users: [] // Don't load existing users for editing, just new ones
    });
    setCurrentCompanyId(company.id);
    setIsEditing(true);
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async () => {
    if (!companyToDelete) return;

    setSubmitting(true);
    setShowConfirmation(false);

    try {
      const response = await companyService.deleteCompany(companyToDelete.id);

      if (response.success) {
        showSuccessToast('Entreprise supprimée avec succès!');
        await loadData();
      } else {
        throw new Error(response.message || 'Échec de la suppression');
      }
    } catch (err) {
      const errorMessage = `Erreur lors de la suppression: ${err.message}`;
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setSubmitting(false);
      setCompanyToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      domain: '',
      status: 'active',
      subscription: {
        status: 'active',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        paymentStatus: 'pending',
        aiAccess: 'both',
        aiEnabled: true,
        notes: ''
      },
      users: []
    });
    setUserForm({
      name: '',
      email: '',
      password: '',
      role: 'simpleuser'
    });
    setIsEditing(false);
    setCurrentCompanyId(null);
    setShowForm(false);
    setShowUserForm(false);
    setError(null);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      setStatusFilter(value);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getSubscriptionStatusType = (subscription) => {
    if (!subscription || subscription.status !== 'active') return 'warning';
    const daysRemaining = getDaysRemaining(subscription.endDate);
    if (daysRemaining <= 3) return 'danger';
    if (daysRemaining <= 7) return 'warning';
    return 'success';
  };

  // Filter and sort data
  // NOTE: Filtering is now largely handled on the server side via loadData
  const filteredCompanies = [...companies]
    .sort((a, b) => {
      if (!sortConfig.key) return 0;

      let aValue = sortConfig.key.includes('subscription.')
        ? a.subscription[sortConfig.key.split('.')[1]]
        : a[sortConfig.key];
      let bValue = sortConfig.key.includes('subscription.')
        ? b.subscription[sortConfig.key.split('.')[1]]
        : b[sortConfig.key];

      if (sortConfig.key === 'createdAt' || sortConfig.key === 'subscription.endDate') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

  // Calculate metrics
  const metrics = {
    total: pagination.total || companies.length,
    active: companies.filter(c => c.status === 'active').length, // This might be inaccurate if paginated, but dashboard is correct
    inactive: companies.filter(c => c.status === 'inactive').length,
    totalUsers: companies.reduce((sum, c) => sum + (c.userCount || 0), 0),
    activeSubscriptions: companies.filter(c => c.subscription.status === 'active').length
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      label: 'Entreprise',
      sortable: true,
      render: (value, company) => (
        <div>
          <div className="font-medium text-gray-900 text-sm">{value}</div>
          <div className="text-gray-500 text-xs">{company.domain || 'Aucun domaine'}</div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (value) => (
        <StatusBadge
          status={value === 'active' ? 'Actif' : 'Inactif'}
          type={value === 'active' ? 'success' : 'danger'}
          size="sm"
        />
      )
    },
    {
      key: 'subscription.status',
      label: 'Abonnement',
      sortable: true,
      render: (_, company) => {
        const subscription = company.subscription;
        const daysRemaining = getDaysRemaining(subscription.endDate);

        let statusText = 'Inactif';
        if (subscription.status === 'active') {
          statusText = `Actif (${daysRemaining}j)`;
        } else if (subscription.status === 'expired') {
          statusText = 'Expiré';
        }

        return (
          <StatusBadge
            status={statusText}
            type={getSubscriptionStatusType(subscription)}
            size="sm"
          />
        );
      }
    },
    {
      key: 'userCount',
      label: 'Utilisateurs',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value || 0}</span>
    },
    {
      key: 'createdAt',
      label: 'Date de création',
      sortable: true,
      render: (value) => (
        <span className="text-gray-500 text-sm">
          {value ? new Date(value).toLocaleDateString('fr-FR') : 'N/A'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, company) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleEdit(company)}
            className="animated-button text-xs text-blue-600 hover:text-white hover:bg-blue-600 font-medium px-3 py-2 rounded-lg transition-all duration-300 flex items-center space-x-1"
            disabled={submitting}
          >
            <i className="fas fa-edit"></i>
            <span>Modifier</span>
          </button>
          <a
            href={`/superadmin/companies/${company.id}/users`}
            className="animated-button text-xs text-green-600 hover:text-white hover:bg-green-600 font-medium px-3 py-2 rounded-lg transition-all duration-300 flex items-center space-x-1"
          >
            <i className="fas fa-users"></i>
            <span>Utilisateurs</span>
          </a>
          <button
            onClick={() => {
              setCompanyToDelete(company);
              setShowConfirmation(true);
            }}
            className="animated-button text-xs text-red-600 hover:text-white hover:bg-red-600 font-medium px-3 py-2 rounded-lg transition-all duration-300 flex items-center space-x-1"
            disabled={submitting}
          >
            <i className="fas fa-trash"></i>
            <span>Supprimer</span>
          </button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="p-6 min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200"></div>
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          </div>
          <h2 className="mt-6 text-xl font-semibold text-gray-700">Chargement des entreprises...</h2>
          <p className="mt-2 text-gray-500">Veuillez patienter pendant que nous récupérons les données</p>
          <div className="mt-4 flex justify-center space-x-1">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Add custom styles */}
      <style>{formStyles}</style>

      {/* Animated Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="form-field">
          <h1 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Gestion des Entreprises
          </h1>
          <p className="text-gray-600 mt-2 text-lg">Créez et gérez les entreprises avec leurs abonnements et utilisateurs</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className={`animated-button px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300 flex items-center space-x-2 ${showForm
              ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
              : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
            } focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={submitting}
        >
          <i className={`fas ${showForm ? 'fa-times' : 'fa-plus'} transition-transform duration-300 ${showForm ? 'rotate-180' : 'rotate-0'}`}></i>
          <span>{showForm ? 'Annuler' : 'Nouvelle Entreprise'}</span>
          {submitting && (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          )}
        </button>
      </div>

      {/* Animated Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="metric-card bg-white p-4 rounded-lg shadow-sm border hover:shadow-lg">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl transition-all duration-300 hover:scale-110">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 mb-1">Total Entreprises</p>
              <p className="text-3xl font-bold text-gray-900 metric-number">{metrics.total}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all duration-1000 ease-out" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>

        <div className="metric-card bg-white p-4 rounded-lg shadow-sm border hover:shadow-lg">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-green-100 to-green-200 rounded-xl transition-all duration-300 hover:scale-110">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 mb-1">Entreprises Actives</p>
              <p className="text-3xl font-bold text-gray-900 metric-number">{metrics.active}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div className="bg-green-600 h-2 rounded-full transition-all duration-1000 ease-out" style={{ width: `${metrics.total > 0 ? (metrics.active / metrics.total) * 100 : 0}%` }}></div>
            </div>
            <span className="ml-2 text-xs text-gray-500">{metrics.total > 0 ? Math.round((metrics.active / metrics.total) * 100) : 0}%</span>
          </div>
        </div>

        <div className="metric-card bg-white p-4 rounded-lg shadow-sm border hover:shadow-lg">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-xl transition-all duration-300 hover:scale-110">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 mb-1">Abonnements Actifs</p>
              <p className="text-3xl font-bold text-gray-900 metric-number">{metrics.activeSubscriptions}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div className="bg-yellow-600 h-2 rounded-full transition-all duration-1000 ease-out" style={{ width: `${metrics.total > 0 ? (metrics.activeSubscriptions / metrics.total) * 100 : 0}%` }}></div>
            </div>
            <span className="ml-2 text-xs text-gray-500">{metrics.total > 0 ? Math.round((metrics.activeSubscriptions / metrics.total) * 100) : 0}%</span>
          </div>
        </div>

        <div className="metric-card bg-white p-4 rounded-lg shadow-sm border hover:shadow-lg">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl transition-all duration-300 hover:scale-110">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 mb-1">Total Utilisateurs</p>
              <p className="text-3xl font-bold text-gray-900 metric-number">{metrics.totalUsers}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div className="bg-purple-600 h-2 rounded-full transition-all duration-1000 ease-out" style={{ width: `${Math.min(100, (metrics.totalUsers / 100) * 100)}%` }}></div>
            </div>
          </div>
        </div>

        <div className="metric-card bg-white p-4 rounded-lg shadow-sm border hover:shadow-lg">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-red-100 to-red-200 rounded-xl transition-all duration-300 hover:scale-110">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 mb-1">Entreprises Inactives</p>
              <p className="text-3xl font-bold text-gray-900 metric-number">{metrics.inactive}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div className="bg-red-600 h-2 rounded-full transition-all duration-1000 ease-out" style={{ width: `${metrics.total > 0 ? (metrics.inactive / metrics.total) * 100 : 0}%` }}></div>
            </div>
            <span className="ml-2 text-xs text-gray-500">{metrics.total > 0 ? Math.round((metrics.inactive / metrics.total) * 100) : 0}%</span>
          </div>
        </div>
      </div>

      {/* Animated Error Message */}
      {error && (
        <div className="message-slide-in bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-lg mb-6 shadow-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="font-medium">{error}</p>
            </div>
            <button
              onClick={loadData}
              className="animated-button ml-4 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 font-medium transition-all duration-300"
            >
              <i className="fas fa-redo mr-2"></i>
              Réessayer
            </button>
          </div>
        </div>
      )}

      {/* Enhanced Animated Company Form */}
      {showForm && (
        <div className="enhanced-company-form bg-white rounded-xl shadow-2xl border border-gray-200 p-8 mb-8 relative overflow-hidden">
          {submitting && (
            <div className="loading-overlay">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                <p className="mt-4 text-gray-600 font-medium">Traitement en cours...</p>
              </div>
            </div>
          )}

          <div className="section-header">
            <i className="fas fa-building section-icon text-blue-600"></i>
            <h2 className="text-xl font-bold text-gray-800">
              {isEditing ? 'Modifier l\'entreprise' : 'Créer une nouvelle entreprise'}
            </h2>
            <div className="ml-auto">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${isEditing ? 'bg-yellow-400' : 'bg-green-400'} animate-pulse`}></div>
                <span className="text-sm text-gray-500">{isEditing ? 'Mode édition' : 'Mode création'}</span>
              </div>
            </div>
          </div>

          {!isEditing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <i className="fas fa-info-circle text-blue-600 mt-1 mr-3"></i>
                <div>
                  <h3 className="text-sm font-semibold text-blue-800 mb-1">Création complète d'entreprise</h3>
                  <p className="text-sm text-blue-700">
                    Configurez l'entreprise, son abonnement et créez directement ses utilisateurs en une seule fois.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Enhanced Basic Company Information */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl form-section border-2 border-transparent">
              <div className="section-header">
                <i className="fas fa-info-circle section-icon text-gray-600"></i>
                <h3 className="text-lg font-semibold text-gray-800">Informations de base</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-field">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-building mr-2 text-blue-500"></i>
                    Nom de l'entreprise *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    className="enhanced-input w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                    required
                    disabled={submitting}
                    placeholder="Entrez le nom de l'entreprise"
                  />
                </div>

                <div className="form-field">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-globe mr-2 text-green-500"></i>
                    Domaine
                  </label>
                  <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => handleFormChange('domain', e.target.value)}
                    className="enhanced-input w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                    placeholder="exemple.com"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="mt-6 form-field">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-toggle-on mr-2 text-purple-500"></i>
                  Statut
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  className="enhanced-input w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                  disabled={submitting}
                >
                  <option value="active">✅ Actif</option>
                  <option value="inactive">❌ Inactif</option>
                </select>
              </div>
            </div>

            {/* Enhanced Subscription Information */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl form-section border-2 border-transparent">
              <div className="section-header">
                <i className="fas fa-calendar-alt section-icon text-blue-600"></i>
                <h3 className="text-lg font-semibold text-gray-800">Configuration de l'abonnement</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="form-field">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-check-circle mr-2 text-blue-500"></i>
                    Statut de l'abonnement
                  </label>
                  <select
                    value={formData.subscription.status}
                    onChange={(e) => handleFormChange('subscription.status', e.target.value)}
                    className="enhanced-input w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                    disabled={submitting}
                  >
                    <option value="active">🟢 Actif</option>
                    <option value="inactive">⚪ Inactif</option>
                    <option value="expired">🔴 Expiré</option>
                    <option value="paused">🟡 En pause</option>
                  </select>
                </div>

                <div className="form-field">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-play mr-2 text-green-500"></i>
                    Date de début
                  </label>
                  <input
                    type="date"
                    value={formData.subscription.startDate}
                    onChange={(e) => handleFormChange('subscription.startDate', e.target.value)}
                    className="enhanced-input w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                    disabled={submitting}
                  />
                </div>

                <div className="form-field">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-stop mr-2 text-red-500"></i>
                    Date de fin
                  </label>
                  <input
                    type="date"
                    value={formData.subscription.endDate}
                    onChange={(e) => handleFormChange('subscription.endDate', e.target.value)}
                    className="enhanced-input w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                    disabled={submitting}
                  />
                </div>

                <div className="form-field">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-credit-card mr-2 text-yellow-500"></i>
                    Statut de paiement
                  </label>
                  <select
                    value={formData.subscription.paymentStatus}
                    onChange={(e) => handleFormChange('subscription.paymentStatus', e.target.value)}
                    className="enhanced-input w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                    disabled={submitting}
                  >
                    <option value="pending">⏳ En attente</option>
                    <option value="paid">✅ Payé</option>
                    <option value="failed">❌ Échec</option>
                    <option value="free">🎁 Gratuit</option>
                  </select>
                </div>

                <div className="form-field">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-robot mr-2 text-indigo-500"></i>
                    Droits d'Accès IA
                  </label>
                  <select
                    value={formData.subscription.aiAccess}
                    onChange={(e) => handleFormChange('subscription.aiAccess', e.target.value)}
                    className="enhanced-input w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                    disabled={submitting}
                  >
                    <option value="both">🌐 Local et Externe</option>
                    <option value="local">🔒 Local uniquement</option>
                    <option value="external">☁️ Externe uniquement</option>
                  </select>
                </div>

                <div className="form-field">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-power-off mr-2 text-green-500"></i>
                    Activer l'IA
                  </label>
                  <label className="relative inline-flex items-center cursor-pointer mt-2">
                    <input
                      type="checkbox"
                      checked={formData.subscription.aiEnabled}
                      onChange={(e) => handleFormChange('subscription.aiEnabled', e.target.checked)}
                      className="sr-only peer"
                      disabled={submitting}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm text-gray-600">
                      {formData.subscription.aiEnabled ? 'Activé' : 'Désactivé'}
                    </span>
                  </label>
                </div>

                <div className="md:col-span-2 form-field">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-sticky-note mr-2 text-purple-500"></i>
                    Notes
                  </label>
                  <textarea
                    value={formData.subscription.notes}
                    onChange={(e) => handleFormChange('subscription.notes', e.target.value)}
                    className="enhanced-input w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 resize-none"
                    rows="3"
                    placeholder="Notes sur l'abonnement..."
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Subscription Summary */}
              {formData.subscription.startDate && formData.subscription.endDate && (
                <div className="subscription-summary">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm opacity-90">Durée de l'abonnement</div>
                      <div className="text-2xl font-bold">{calculateSubscriptionDays()} jours</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm opacity-90">Statut</div>
                      <div className="text-lg font-semibold capitalize">{formData.subscription.status}</div>
                    </div>
                  </div>
                  {formData.subscription.notes && (
                    <div className="mt-3 pt-3 border-t border-white border-opacity-20">
                      <div className="text-sm opacity-90">Notes</div>
                      <div className="text-sm">{formData.subscription.notes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Users Management - Only for new companies */}
            {!isEditing && (
              <div className="bg-green-50 p-4 rounded-lg form-section">
                <div className="section-header">
                  <i className="fas fa-users section-icon text-green-600"></i>
                  <h3 className="text-md font-semibold text-gray-800">
                    Utilisateurs à créer ({formData.users.length})
                  </h3>
                  <div className="ml-auto"></div>
                </div>

                <div className="flex justify-end mb-3">
                  <button
                    type="button"
                    onClick={() => setShowUserForm(!showUserForm)}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm transition-colors flex items-center"
                    disabled={submitting}
                  >
                    <i className={`fas ${showUserForm ? 'fa-minus' : 'fa-plus'} mr-2`}></i>
                    {showUserForm ? 'Annuler' : 'Ajouter un utilisateur'}
                  </button>
                </div>

                {/* User Form */}
                {showUserForm && (
                  <div className="bg-white p-4 rounded-md border mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Nouvel utilisateur</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Nom complet *
                        </label>
                        <input
                          type="text"
                          value={userForm.name}
                          onChange={(e) => handleUserFormChange('name', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                          placeholder="Jean Dupont"
                          disabled={submitting}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={userForm.email}
                          onChange={(e) => handleUserFormChange('email', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                          placeholder="jean@entreprise.com"
                          disabled={submitting}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Mot de passe *
                        </label>
                        <input
                          type="password"
                          value={userForm.password}
                          onChange={(e) => handleUserFormChange('password', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                          placeholder="••••••••"
                          disabled={submitting}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Rôle
                        </label>
                        <select
                          value={userForm.role}
                          onChange={(e) => handleUserFormChange('role', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                          disabled={submitting}
                        >
                          <option value="simpleuser">Utilisateur simple</option>
                          <option value="admin">Administrateur</option>
                        </select>
                      </div>
                    </div>
                    {userForm.password && (
                      <PasswordRequirements password={userForm.password} />
                    )}
                    <div className="flex justify-end space-x-2 mt-3">
                      <button
                        type="button"
                        onClick={() => setShowUserForm(false)}
                        className="px-3 py-1 text-gray-600 bg-gray-200 rounded text-sm hover:bg-gray-300 transition-colors"
                        disabled={submitting}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={addUserToCompany}
                        className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                        disabled={submitting}
                      >
                        Ajouter
                      </button>
                    </div>
                  </div>
                )}

                {/* Users List */}
                {formData.users.length > 0 && (
                  <div className="bg-white rounded-md border overflow-hidden user-table">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nom
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Rôle
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {formData.users.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.role === 'admin'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-blue-100 text-blue-800'
                                }`}>
                                {user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                type="button"
                                onClick={() => removeUserFromCompany(user.id)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                disabled={submitting}
                              >
                                <i className="fas fa-trash text-xs"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {formData.users.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Aucun utilisateur ajouté. Cliquez sur "Ajouter un utilisateur" pour commencer.
                  </div>
                )}
              </div>
            )}

            {/* Enhanced Form Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={resetForm}
                className="animated-button px-6 py-3 text-gray-700 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg hover:from-gray-300 hover:to-gray-400 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-all duration-300 font-medium flex items-center space-x-2"
                disabled={submitting}
              >
                <i className="fas fa-times"></i>
                <span>Annuler</span>
              </button>
              <button
                type="submit"
                className="animated-button px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center space-x-2 min-w-[140px] justify-center"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Traitement...</span>
                  </>
                ) : (
                  <>
                    <i className={`fas ${isEditing ? 'fa-save' : 'fa-plus'}`}></i>
                    <span>{isEditing ? 'Mettre à jour' : 'Créer l\'entreprise'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search and filters */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filters={[
          {
            key: 'status',
            label: 'Filtrer par statut',
            type: 'select',
            value: statusFilter,
            options: [
              { value: 'all', label: 'Tous les statuts' },
              { value: 'active', label: 'Actif' },
              { value: 'inactive', label: 'Inactif' }
            ]
          }
        ]}
        onFilterChange={handleFilterChange}
        onResetFilters={resetFilters}
        placeholder="Rechercher par nom d'entreprise ou domaine..."
      />

      {/* Data table */}
      <DataTable
        data={filteredCompanies}
        columns={columns}
        isLoading={loading}
        onSort={handleSort}
        sortConfig={sortConfig}
        emptyMessage="Aucune entreprise trouvée."
        searchQuery={searchTerm}
        filters={{ status: statusFilter }}
      />

      {/* Pagination */}
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
      />

      {/* Results summary */}
      {filteredCompanies.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg text-sm text-gray-600">
          Affichage de {filteredCompanies.length} entreprise(s) sur {pagination.total} au total
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setCompanyToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Supprimer l'entreprise"
        message={`Êtes-vous sûr de vouloir supprimer l'entreprise "${companyToDelete?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        confirmButtonClass="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
        cancelButtonClass="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
      />
    </div>
  );
};

export default CompanyManagement;